import { useCallback, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageShell } from '~/components/layout';
import {
  CtaBanner,
  FaqAccordion,
  FeatureBlock,
  Hero,
  PayrollClosing,
  PayrollOfferModal,
  PayrollPromoBanner,
  PayrollSavings,
  TextSection,
} from '~/components/sections';
import {
  payrollFaq,
  payrollFaqFootnote,
  payrollFounders,
  payrollFree,
  payrollHero,
  payrollTeam,
} from '~/content/payroll';
import { trustedBySection } from '~/content/shared';
import { faqPageJsonLd, pageHeadMeta } from '~/lib/seo';
import heroCss from '~/styles/hero.css?inline';

export const Route = createFileRoute('/payroll')({
  head: () => {
    const head = pageHeadMeta({
      title: 'Pay Your Team Globally With $0 Payroll Fees',
      description:
        'Move your payroll to nsave: no subscription, per-employee or transfer fees, plus complimentary virtual Visa Platinum cards for your team and Visa Infinite cards for founders.',
      path: '/payroll',
      inlineStyles: [heroCss],
    });
    return { ...head, scripts: [...head.scripts, faqPageJsonLd(payrollFaq)] };
  },
  component: PayrollPage,
});

const payrollTrust = {
  ...trustedBySection,
  body: 'We are on a mission to make global money movement secure, transparent, and accessible for everyone. Pay for the tools and services you need, wherever Visa is accepted. Set up your card in minutes and manage it from the app.',
  // The mobile design keeps the site-wide "Trusted by" copy.
  mobileBody: trustedBySection.body,
  link: undefined,
};

// The three feature blocks sit 20px apart on desktop, as one stacked image column.
const featureSpacing = '!py-[60px] lg:!py-2.5';

function PayrollPage() {
  const [offerOpen, setOfferOpen] = useState(false);
  const closeOffer = useCallback(() => setOfferOpen(false), []);

  return (
    <PageShell>
      <Hero
        content={payrollHero}
        secondaryCtaVariant="secondary"
        // Mobile Figma: 36px headline, CTAs 30px below it.
        titleClassName="max-md:!text-[36px] max-md:!leading-[1.16] max-md:!tracking-[-0.022em]"
        ctaClassName="max-md:!mt-[30px]"
        statsSlot={
          <PayrollPromoBanner
            onLearnMore={() => setOfferOpen(true)}
            className="mt-[106px] mb-[9px] md:mt-[146px] md:mb-0"
          />
        }
      />
      <TextSection content={payrollTrust} sectionClassName="!mt-10 !mb-10 lg:!mt-5 lg:!mb-[70px]" />
      <FeatureBlock content={payrollFree} imagePosition="left" sectionClassName={featureSpacing} />
      <FeatureBlock content={payrollTeam} imagePosition="right" sectionClassName={featureSpacing} />
      <FeatureBlock
        content={payrollFounders}
        imagePosition="left"
        sectionClassName={featureSpacing}
      />
      <PayrollSavings className="!pt-[60px] !pb-[60px] lg:!pt-[150px] lg:!pb-[90px]" />
      <PayrollClosing className="!py-[60px] lg:!py-[90px]" />
      <FaqAccordion
        content={payrollFaq}
        gridAligned
        toggleIcon="chevron"
        footnote={payrollFaqFootnote}
        sectionClassName="!pt-10 !pb-[100px] lg:!pt-[70px] lg:!pb-[140px]"
      />
      <CtaBanner />
      <PayrollOfferModal open={offerOpen} onClose={closeOffer} />
    </PageShell>
  );
}
